// Config + identifiers for the in-app rating prompt (mobile native review +
// extension/webapp store redirect). No magic numbers/strings live in the
// feature code; everything tunable sits here.

// Engagement floor before we ever soft-ask (items = bookmarks + notes).
export const MIN_ITEMS = 10
export const MIN_ACCOUNT_AGE_DAYS = 3

// Re-ask cadence after a "maybe later" (dismiss without answering).
export const REASK_COOLDOWN_DAYS = 30
export const MAX_SHOWS = 3

// A positive answer completes the target, but not forever: the OS can silently
// suppress the native review sheet (iOS allows 3 requests/year and reports
// nothing back), so a user who said yes may never have been shown anything.
// After this long the target becomes eligible again, still subject to MAX_SHOWS.
export const COMPLETED_REASK_DAYS = 365

// Staging builds (any client) shorten the re-ask cooldown to 3 minutes so the
// prompt can be exercised repeatedly. Production keeps REASK_COOLDOWN_DAYS.
// eslint-disable-next-line no-undef
export const IS_STAGING = typeof CONFIG !== 'undefined' && CONFIG?.sentry?.environment === 'staging'
const DAY_MS = 24 * 60 * 60 * 1000

export const REASK_COOLDOWN_MS = IS_STAGING
  ? 3 * 60 * 1000
  : REASK_COOLDOWN_DAYS * DAY_MS

export const COMPLETED_REASK_MS = COMPLETED_REASK_DAYS * DAY_MS

// Backend user-settings key holding the per-target state (stored as a JSON
// string in the existing {name, value}[] settings shape).
export const SETTINGS_KEY = 'ratingPrompt'

// Device-storage key holding the queued trigger (see requestRatingPrompt). It
// lives in storage rather than module scope because the surface that queues it
// is frequently NOT the surface that shows it: Android's share Activity runs a
// second WebView that finishes itself the moment MainActivity comes to front.
export const PENDING_TRIGGER_KEY = 'ratingPromptPending'

// A queued trigger is only worth flushing while the moment is still fresh; past
// this the user has moved on and the ask would land out of context.
export const PENDING_TRIGGER_TTL_MS = 10 * 60 * 1000

// Moments that may surface the rating soft-ask. Add a member here and call
// requestRatingPrompt(RATING_TRIGGER.X) from that surface to opt a new one in.
export const RATING_TRIGGER = {
  ITEM_ADD_DETAILS: 'item_add_details',
  ITEM_ADDED: 'item_added',
  // Clients with no native review sheet (extension, webapp) ask on open rather
  // than off the back of a specific action.
  APP_OPEN: 'app_open',
}

// Answers to the soft-ask, shared by the modal and the banner.
export const RATING_RESULT = {
  POSITIVE: 'positive',
  FEEDBACK: 'feedback',
  NEGATIVE: 'negative',
}

// Steps of the two-step soft-ask: the opening question, then the "sorry to hear
// that" follow-up shown after a negative answer.
export const RATING_STEP = {
  ASK: 'ask',
  FEEDBACK: 'feedback',
}

// $bus events: analytics hooks.
export const EVENTS = {
  SHOWN: 'rating:shown',
  ACCEPTED: 'rating:accepted',
  DECLINED: 'rating:declined',
}

// Review targets. Native targets map to app stores; ext_* targets are the
// browser extension stores, shared by the extension popup AND the webapp
// running in that same browser (so rating via either path suppresses both).
export const TARGET = {
  IOS: 'ios',
  ANDROID: 'android',
  EXT_CHROME: 'ext_chrome',
  EXT_FIREFOX: 'ext_firefox',
  EXT_EDGE: 'ext_edge',
  EXT_SAFARI: 'ext_safari',
}

// Apple App ID, required by AppReview.openAppStore() on iOS (fallback path).
export const IOS_APP_ID = '1673761012'

// Maps a Bowser browser name -> extension target + storeLinks/config key.
// Edge first: Chromium Edge's UA also contains "chrome".
export const EXTENSION_BROWSERS = [
  { match: 'edge', target: TARGET.EXT_EDGE, linkKey: 'edge' },
  { match: 'chrome', target: TARGET.EXT_CHROME, linkKey: 'chrome' },
  { match: 'firefox', target: TARGET.EXT_FIREFOX, linkKey: 'firefox' },
  { match: 'safari', target: TARGET.EXT_SAFARI, linkKey: 'safari' },
]
